import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import {
  MyCalendarItem, MyCalendarSlot, MyCalendarTooltip, compareItems, coversDay, parseDateKey, tooltipFor
} from '../my-calendar.models';
import { RESOURCE_TOOLTIP_POSITIONS } from '../../shared/assignment-tooltip/resource-assignment-tooltip.component';

const MAX_PER_DAY: Record<'normal' | 'compact' | 'mini', number> = { normal: 3, compact: 2, mini: 3 };

/**
 * Monatsansicht von „Mein Kalender“: 6 Wochen à 7 Tage, je Tag die Einträge
 * (mehrtägige an jedem betroffenen Tag). Klick auf einen Tag legt einen Termin an,
 * „+ n weitere“ öffnet die Woche des Tages.
 */
@Component({
  selector: 'app-resource-my-calendar-month',
  templateUrl: './resource-my-calendar-month.component.html',
  styleUrls: ['../my-calendar-items.css', './resource-my-calendar-month.component.css']
})
export class ResourceMyCalendarMonthComponent implements OnChanges {
  /** Erster Tag des angezeigten Monats (yyyy-MM-dd). */
  @Input() month = '';
  /** 42 Tage ab dem Montag vor dem Monatsersten. */
  @Input() days: string[] = [];
  @Input() items: MyCalendarItem[] = [];
  @Input() today = '';
  @Input() selectable = true;
  /** normal = Monatsansicht, compact = Quartal (3 Monate), mini = Jahr (Punkte je Tag). */
  @Input() density: 'normal' | 'compact' | 'mini' = 'normal';
  /** Monatsname über dem Raster (Quartal/Jahr). */
  @Input() heading = '';
  @Output() itemClick = new EventEmitter<MyCalendarItem>();
  @Output() slotSelect = new EventEmitter<MyCalendarSlot>();
  @Output() dayOpen = new EventEmitter<string>();

  readonly weekdays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  readonly tooltipPositions = RESOURCE_TOOLTIP_POSITIONS;

  /** Eintrag unter der Maus (Tooltip wie in der Teamplanung). */
  hoverKey: string | null = null;

  tooltip(item: MyCalendarItem): MyCalendarTooltip { return tooltipFor(item); }
  get maxPerDay(): number { return MAX_PER_DAY[this.density]; }
  itemsByDay = new Map<string, MyCalendarItem[]>();

  ngOnChanges(): void {
    const map = new Map<string, MyCalendarItem[]>();
    for (const day of this.days) {
      map.set(day, this.items.filter(i => coversDay(i, day)).sort(compareItems));
    }
    this.itemsByDay = map;
  }

  dayItems(day: string): MyCalendarItem[] {
    return this.itemsByDay.get(day) ?? [];
  }

  inMonth(day: string): boolean {
    return day.substring(0, 7) === this.month.substring(0, 7);
  }

  dayNumber(day: string): number {
    return parseDateKey(day).getDate();
  }

  /** Uhrzeit nur bei eintägigen Terminen bzw. am ersten Tag. */
  timePrefix(item: MyCalendarItem, day: string): string {
    if (item.allDay) return '';
    return item.startDate === day ? item.startTime : '';
  }

  onDayClick(event: MouseEvent, day: string): void {
    if ((event.target as HTMLElement).closest('.entry, .more')) return;
    // Jahresansicht: Tag öffnen statt direkt anlegen.
    if (this.density === 'mini') { this.dayOpen.emit(day); return; }
    if (!this.selectable) return;
    this.slotSelect.emit({ startDate: day, startTime: '08:00', endDate: day, endTime: '09:00' });
  }

  trackDay(_i: number, day: string): string { return day; }
  trackItem(_i: number, item: MyCalendarItem): string { return item.key; }
}
